import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Loader2, AlertCircle, Database, Cloud, FileText, Radio, Upload } from 'lucide-react';
import exploreApi from './exploreApi';
import { connectorDefinitions } from './connectors';

const ICON_MAP = { Database, Cloud, FileText, Radio, Upload };

function ConnectorIcon({ iconName, className }) {
  const Icon = ICON_MAP[iconName] || Database;
  return <Icon className={className} />;
}

export function renderField(field, value, onChange) {
  switch (field.type) {
    case 'text':
    case 'password':
    case 'number':
      return (
        <div key={field.name} className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700">
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </label>
          <input
            type={field.type}
            value={typeof value === 'string' || typeof value === 'number' ? String(value) : ''}
            onChange={e => onChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          {field.helpText && <p className="text-xs text-gray-500">{field.helpText}</p>}
        </div>
      );

    case 'textarea':
      return (
        <div key={field.name} className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700">
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </label>
          <textarea
            value={typeof value === 'string' ? value : ''}
            onChange={e => onChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            rows={6}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
          />
          {field.helpText && <p className="text-xs text-gray-500">{field.helpText}</p>}
        </div>
      );

    case 'select':
      return (
        <div key={field.name} className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700">
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </label>
          <select
            value={typeof value === 'string' ? value : ''}
            onChange={e => onChange(field.name, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="">Select…</option>
            {field.options?.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {field.helpText && <p className="text-xs text-gray-500">{field.helpText}</p>}
        </div>
      );

    case 'checkbox':
      return (
        <div key={field.name} className="flex items-center gap-2">
          <input
            type="checkbox"
            id={`field-${field.name}`}
            checked={!!value}
            onChange={e => onChange(field.name, e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          />
          <label htmlFor={`field-${field.name}`} className="text-sm font-medium text-gray-700 cursor-pointer">
            {field.label}
          </label>
        </div>
      );

    default:
      return null;
  }
}

export default function NewSource() {
  const navigate = useNavigate();
  const [step, setStep] = useState('select');
  const [selectedConnector, setSelectedConnector] = useState(null);
  const [sourceName, setSourceName] = useState('');
  const [config, setConfig] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('supported');

  const supportedConnectors = connectorDefinitions.filter(c => c.supported);
  const comingSoonConnectors = connectorDefinitions.filter(c => !c.supported);

  const handleSelectConnector = (connector) => {
    if (!connector.supported) return;
    setSelectedConnector(connector);
    setStep('configure');
    setSourceName(`My ${connector.name}`);
    const defaultConfig = {};
    if (connector.id === 'sqlserver') {
      defaultConfig.encrypt = true;
      defaultConfig.trust_server_certificate = true;
    }
    setConfig(defaultConfig);
    setTestResult(null);
    setError(null);
  };

  const handleConfigChange = (fieldName, value) => {
    setConfig(prev => ({ ...prev, [fieldName]: value }));
    setTestResult(null);
  };

  const validateConfig = () => {
    if (!selectedConnector) return false;
    for (const field of selectedConnector.fields) {
      if (field.required && !config[field.name]) {
        setError(`${field.label} is required`);
        return false;
      }
    }
    if (!sourceName.trim()) {
      setError('Source name is required');
      return false;
    }
    return true;
  };

  const handleTestConnection = async () => {
    if (!validateConfig() || !selectedConnector) return;
    setTesting(true);
    setTestResult(null);
    setError(null);
    let tempSourceId = null;
    try {
      const tempSource = await exploreApi.createSource({
        name: `test_${Date.now()}`,
        type: 'source',
        connector: selectedConnector.id,
        config,
        status: 'testing',
      });
      tempSourceId = tempSource.id;
      await exploreApi.testConnection(tempSource.id);
      await exploreApi.deleteSource(tempSource.id);
      setTestResult({ success: true, message: 'Connection successful! All credentials are valid.' });
    } catch (err) {
      if (tempSourceId) {
        try { await exploreApi.deleteSource(tempSourceId); } catch {}
      }
      setTestResult({ success: false, message: err.message || 'Connection failed' });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!validateConfig() || !selectedConnector) return;
    if (!testResult?.success) {
      setError('Please test the connection successfully before saving.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await exploreApi.createSource({
        name: sourceName,
        type: 'source',
        connector: selectedConnector.id,
        config,
        status: 'connected',
        metadata: { category: selectedConnector.category, configured_at: new Date().toISOString() },
      });
      navigate('/data-studio', { state: { tab: 'analyzeData' } });
    } catch (err) {
      setError(err.message || 'Failed to save source');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/20">
      <div className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => step === 'configure' ? setStep('select') : navigate('/data-studio', { state: { tab: 'analyzeData' } })}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {step === 'select' ? 'Select Connector' : `Configure ${selectedConnector?.name}`}
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                {step === 'select' ? 'Choose a data source to connect' : 'Enter connection details'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {step === 'select' && (
          <div className="space-y-6">
            <div className="flex gap-1 border-b border-gray-200">
              <button
                onClick={() => setActiveTab('supported')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                  activeTab === 'supported' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Supported Connectors ({supportedConnectors.length})
              </button>
              <button
                onClick={() => setActiveTab('coming-soon')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                  activeTab === 'coming-soon' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Coming Soon ({comingSoonConnectors.length})
              </button>
            </div>

            {activeTab === 'supported' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {supportedConnectors.map(connector => (
                  <button
                    key={connector.id}
                    onClick={() => handleSelectConnector(connector)}
                    className="text-left bg-white border-2 border-gray-200 rounded-xl p-5 hover:border-primary hover:shadow-lg transition-all"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2.5 bg-blue-50 rounded-lg">
                        <ConnectorIcon iconName={connector.icon} className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{connector.name}</p>
                        <span className="inline-block px-2 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-medium mt-0.5">Supported</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">{connector.description}</p>
                  </button>
                ))}
              </div>
            )}

            {activeTab === 'coming-soon' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {comingSoonConnectors.map(connector => (
                  <div key={connector.id} className="bg-white border-2 border-dashed border-gray-200 rounded-xl p-5 opacity-60">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2.5 bg-gray-50 rounded-lg">
                        <ConnectorIcon iconName={connector.icon} className="h-5 w-5 text-gray-400" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-700 text-sm">{connector.name}</p>
                        <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-[10px] font-medium mt-0.5">Coming Soon</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">{connector.description}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 'configure' && selectedConnector && (
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-blue-50 border-b border-gray-200">
                <p className="font-semibold text-gray-900">Source Details</p>
                <p className="text-xs text-gray-500 mt-0.5">Give your source a name</p>
              </div>
              <div className="p-6 space-y-1.5">
                <label className="block text-sm font-medium text-gray-700">Source Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={sourceName}
                  onChange={e => setSourceName(e.target.value)}
                  placeholder="e.g., Production PostgreSQL"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-blue-50 border-b border-gray-200">
                <p className="font-semibold text-gray-900">Connection Configuration</p>
                <p className="text-xs text-gray-500 mt-0.5">Enter the connection details for {selectedConnector.name}</p>
              </div>
              <div className="p-6 space-y-5">
                {selectedConnector.fields.map(field => renderField(field, config[field.name] ?? '', handleConfigChange))}
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {testResult && (
              <div className={`flex items-center gap-3 p-4 border rounded-lg ${testResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                {testResult.success
                  ? <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                  : <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />}
                <p className={`text-sm font-medium ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>{testResult.message}</p>
              </div>
            )}

            <div className="flex gap-4">
              <button
                onClick={handleTestConnection}
                disabled={testing}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                {testing ? <><Loader2 className="h-4 w-4 animate-spin" /> Testing…</> : 'Test Connection'}
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !testResult?.success}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primaryDark disabled:opacity-50 transition-colors"
              >
                {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : <><Check className="h-4 w-4" /> Save &amp; Continue</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
